import {
  I20LcgsByBay,
  IBayPattern,
  IRowPattern,
  PairedStackContainer,
  SmartCGs,
  SmartPairedBayBlock,
  SmartUnpairedBayBlock,
  UnpairedStackContainer,
} from "@tedivo/tedivo-bay-grid-pure";
import { pad3, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

import { BayLevelEnum } from "open-vessel-definition";

/** What the deck view's tier shading counts. `none` keeps the colour-by symbols. */
export type TDeckTiersMode = "none" | "below" | "above" | "both";

export const DECK_TIERS_MODES: TDeckTiersMode[] = ["none", "below", "above", "both"];

export interface IDeckTiersByStack {
  /** The odd bays of the stack's bay group, sorted: one for a 20'-only bay, two for a 40' pair */
  bays: IBayPattern[];
  row: IRowPattern;
  /** LCG of the group's centre, in mm: the single 20' LCG, or the midpoint of the pair's */
  lcg: number;
  /** TCG of the row, in mm, from the containers stacked there */
  tcg: number;
  /** Tiers stacked for the mode: hold, deck, or hold + deck */
  count: number;
}

type TBlock = SmartUnpairedBayBlock | SmartPairedBayBlock;
type TLevel = BayLevelEnum.ABOVE | BayLevelEnum.BELOW;

/**
 * The number of tiers stacked in every occupied stack of the deck view, one
 * entry per bay group and row.
 *
 * Bay groups follow the top bay numbers (createSvgTopBayNumbers): a bay with a
 * `confirmedPairedBay` and its partner make one group, so a 40' pair is one
 * group of stacks, not two. That pairing is decided over both
 * levels, whereas the smart blocks are built per level, so the blocks are looked
 * up per level rather than grouped.
 *
 * The count per stack is its occupied tiers at the level(s) of the mode (two
 * 20s side by side in a tier count as one). A stack with cargo at the other
 * level only is still listed, with 0, so its symbol shows the stack is empty
 * for what is being counted. Stacks with no cargo at all are left out.
 */
export function countDeckTiersByStack(
  smartCGs: SmartCGs,
  lcgsBy20Bay: I20LcgsByBay,
  mode: Exclude<TDeckTiersMode, "none">,
): IDeckTiersByStack[] {
  const countedLevels: TLevel[] =
    mode === "below"
      ? [BayLevelEnum.BELOW]
      : mode === "above"
        ? [BayLevelEnum.ABOVE]
        : [BayLevelEnum.BELOW, BayLevelEnum.ABOVE];

  const allBays = Object.keys(lcgsBy20Bay).sort(sortNumericAsc) as IBayPattern[];
  const handled = new Set<IBayPattern>();
  const stacks: IDeckTiersByStack[] = [];

  allBays.forEach((bay) => {
    if (handled.has(bay)) return;

    const lcgsOfBay = lcgsBy20Bay[bay];
    const lcg = lcgsOfBay.lcgs.get(20)?.lcg;
    if (lcg === undefined) return;

    handled.add(bay);

    const pairedBay = lcgsOfBay.confirmedPairedBay;
    const pairedLcg = pairedBay ? lcgsBy20Bay[pairedBay]?.lcgs.get(20)?.lcg : undefined;

    const bays: IBayPattern[] = [bay];
    const lookupBays: IBayPattern[] = [bay];
    let groupLcg = lcg;

    if (pairedBay && pairedLcg !== undefined) {
      handled.add(pairedBay);
      bays.push(pairedBay);
      bays.sort(sortNumericAsc);

      // The paired block is registered under the even bay as well
      const evenBay = pad3(Math.round((Number(bay) + Number(pairedBay)) / 2)) as IBayPattern;
      lookupBays.push(evenBay, pairedBay);

      groupLcg = (lcg + pairedLcg) / 2;
    }

    // Per row: tiers at each level, and the row's TCG from any container in it
    const rows = new Map<IRowPattern, { tcg: number | undefined; tiers: Record<TLevel, number> }>();

    ([BayLevelEnum.BELOW, BayLevelEnum.ABOVE] as TLevel[]).forEach((level) => {
      blocksOf(smartCGs, lookupBays, level).forEach((block) => {
        block.getSmartStacksFromPortToStarboard().forEach((stack) => {
          const tiers = stack.getTiersFromBottomTierToTop();
          if (tiers.length === 0) return;

          const row = stack.stack;
          const entry = rows.get(row) ?? {
            tcg: undefined,
            tiers: { [BayLevelEnum.BELOW]: 0, [BayLevelEnum.ABOVE]: 0 } as Record<TLevel, number>,
          };

          entry.tiers[level] = Math.max(entry.tiers[level], tiers.length);
          if (entry.tcg === undefined) entry.tcg = tcgOf(tiers);

          rows.set(row, entry);
        });
      });
    });

    rows.forEach((entry, row) => {
      if (entry.tcg === undefined) return;

      stacks.push({
        bays,
        row,
        lcg: groupLcg,
        tcg: entry.tcg,
        count: countedLevels.reduce((sum, level) => sum + entry.tiers[level], 0),
      });
    });
  });

  return stacks;
}

/** The distinct blocks of a level that hold the given bays */
function blocksOf(smartCGs: SmartCGs, bays: IBayPattern[], level: TLevel): TBlock[] {
  const blocks = new Set<TBlock>();

  bays.forEach((bay) => {
    const block = smartCGs.smartBayBlocksByBay[bay]?.[level];
    if (block) blocks.add(block);
  });

  return Array.from(blocks);
}

/** The TCG of the first container found in the stack's tiers */
function tcgOf(tiers: Array<UnpairedStackContainer | PairedStackContainer>): number | undefined {
  for (const tier of tiers) {
    for (const container of tier.containers) {
      // A container without CGs is reported elsewhere; it has no position here
      const tcg = "cgs" in container ? container.cgs?.tcg : undefined;
      if (tcg !== undefined) return tcg;
    }
  }
  return undefined;
}

/**
 * The counts keyed `"${oddBay}-${row}"`, one key per odd bay of the group, so a
 * container can look its stack up by its own odd bay (`isoOddBay`) and row.
 */
export function tiersCountByStackKey(stacks: IDeckTiersByStack[]): Map<string, number> {
  const byKey = new Map<string, number>();

  stacks.forEach((stack) => {
    stack.bays.forEach((bay) => byKey.set(`${bay}-${stack.row}`, stack.count));
  });

  return byKey;
}

/** The tallest stack on board, at least 1 so a ramp over it never divides by zero */
export function maxTiersCount(stacks: IDeckTiersByStack[]): number {
  return stacks.reduce((max, stack) => Math.max(max, stack.count), 1);
}

/**
 * The top of the shading ramp: the taller of the tallest hold stack and the
 * tallest deck stack on board, at least 1. One scale for both levels, so the
 * hold and the deck of the same vessel are comparable, and never the hold+deck
 * sum, which no single level reaches.
 */
export function deckTiersRampMax(smartCGs: SmartCGs, lcgsBy20Bay: I20LcgsByBay): number {
  return Math.max(
    maxTiersCount(countDeckTiersByStack(smartCGs, lcgsBy20Bay, "below")),
    maxTiersCount(countDeckTiersByStack(smartCGs, lcgsBy20Bay, "above")),
  );
}
